import { put } from '@Green-Dot-Corporation/eureka-lib-fetch-utils';
import { augmentHeaders, parseError } from './apiUtils';

export default {};

/**
 * after login, server returns accessToken and refreshToken
 * To make a post api call, we need accessToken in the header
 * when accessToken expired, we use refreshToken in the header to get new accessToken
 * when refreshToken expired, we log user out, clear cache and start over
 */
export const REFRESH_TOKEN = 'RefreshToken';
export const ACCESS_TOKEN = 'AccessToken';

const REFRESH_TOKEN_HEADER = 'refresh-token';
const AUTHORIZATION_TOKEN_HEADER = 'Authorization';

export const augment = (options, isLegacy = false) => {
    const defaultHeaders = {};
    const { headers = {}, ...rest } = options ?? {};
    const refreshToken = sessionStorage.getItem(REFRESH_TOKEN);
    const accessToken = sessionStorage.getItem(ACCESS_TOKEN);

    if (refreshToken) {
        defaultHeaders[REFRESH_TOKEN_HEADER] = refreshToken;
    }

    if (accessToken) {
        defaultHeaders[AUTHORIZATION_TOKEN_HEADER] = `Bearer ${accessToken}`;
    }

    return augmentHeaders(
        {
            ...rest,
            headers: { ...defaultHeaders, ...headers },
        },
        isLegacy,
    );
};

/**
 * This is used in post-login scenario. It throws exception if used in pre-login case.
 * @returns Promise -> accessToken
 */
export const fetchAccessToken = async () => {
    const accessToken = sessionStorage.getItem(ACCESS_TOKEN);

    // token is still valid
    if (accessToken) return accessToken;

    // if post-login
    const refreshToken = sessionStorage.getItem(REFRESH_TOKEN);

    if (refreshToken) {
        const { accessToken: newToken } = await put(
            '/api/auth/token/accesstoken',
            augment(),
        );

        return newToken;
    }

    return undefined;
};

export const authenticatedFetch = async (method, url, options) => {
    try {
        const accessToken = await fetchAccessToken();
        const { headers: otherHeaders, ...rest } = options ?? {};
        const headers = {
            [AUTHORIZATION_TOKEN_HEADER]:
                accessToken && `Bearer ${accessToken}`,
            ...otherHeaders,
        };

        if (headers[AUTHORIZATION_TOKEN_HEADER]) {
            return await method(url, { headers, ...rest });
        }

        throw new Error('Missing accessToken');
    } catch (error) {
        const errorResult = parseError(error);
        const { errorCode } = errorResult;

        // 401 = accessToken expired
        if (errorCode === 401) {
            try {
                sessionStorage.removeItem(ACCESS_TOKEN);
                const accessToken = await fetchAccessToken();

                if (accessToken) {
                    sessionStorage.setItem(ACCESS_TOKEN, accessToken);

                    return authenticatedFetch(method, url, options);
                }

                throw new Error('Invalid accessToken');
            } catch (accessError) {
                sessionStorage.clear();

                return undefined;
            }
        }

        // other errors will need to be handled case by case by the invoker
        return errorResult;
    }
};
